import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Loader2, Lock, Sparkles } from 'lucide-react';

export interface ResumeProfileHints {
  phone?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  dateOfBirth?: string | null;
  gender?: 'male' | 'female' | null;
  maritalStatus?: 'married' | 'unmarried' | null;
}

export interface ResumePreviewAnalysis {
  atsScore: number;
  atsSummary: string;
  improvements: Array<{
    area: string;
    severity: 'high' | 'medium' | 'low';
    suggestion: string;
  }>;
  keywordSuggestions: string[];
  recommendedSkills?: Array<{
    skill: string;
    reason: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  profileHints?: ResumeProfileHints | null;
}

export function suggestSkillsFromAnalysis(analysis: ResumePreviewAnalysis): {
  primary: string;
  secondary: string;
} {
  const keywords = [...new Set(analysis.keywordSuggestions || [])];
  const recommended = analysis.recommendedSkills || [];
  const byPriority = (p: 'high' | 'medium' | 'low') =>
    recommended.filter((s) => s.priority === p).map((s) => s.skill);

  const primaryList = keywords.length
    ? keywords.slice(0, 6)
    : [...byPriority('high'), ...byPriority('medium')].slice(0, 6);

  const secondaryList = keywords.length
    ? [...byPriority('high'), ...byPriority('medium'), ...byPriority('low')]
        .filter((s) => !primaryList.includes(s))
        .slice(0, 6)
    : byPriority('low').slice(0, 6);

  return {
    primary: primaryList.join(', '),
    secondary: secondaryList.join(', '),
  };
}

export function suggestProfileFromAnalysis(analysis: ResumePreviewAnalysis): ResumeProfileHints {
  const h = analysis.profileHints;
  if (!h) return {};
  return {
    phone: h.phone || undefined,
    addressLine1: h.addressLine1 || undefined,
    city: h.city || undefined,
    state: h.state || undefined,
    country: h.country || undefined,
    pincode: h.pincode || undefined,
    dateOfBirth: h.dateOfBirth || undefined,
    gender: h.gender === 'male' || h.gender === 'female' ? h.gender : undefined,
    maritalStatus:
      h.maritalStatus === 'married' || h.maritalStatus === 'unmarried'
        ? h.maritalStatus
        : undefined,
  };
}

const severityColor: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-blue-100 text-blue-800',
};

interface Props {
  analyzing?: boolean;
  error?: string;
  analysis: ResumePreviewAnalysis | null;
  fileName?: string;
  overlayMessage?: string;
  subtitle?: string;
}

const ResumeAnalysisBlurredPreview: React.FC<Props> = ({
  analyzing = false,
  error = '',
  analysis,
  fileName,
  overlayMessage = 'Create your account to unlock the full report',
  subtitle = 'Complete registration to see detailed feedback, skills, and certifications on your profile.',
}) => {
  if (analyzing) {
    return (
      <div className="mt-4 rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50/80 to-white p-6">
        <div className="flex flex-col items-center gap-3 py-4">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <p className="text-sm font-medium text-slate-700">
            Analyzing {fileName || 'your resume'}…
          </p>
          <p className="text-xs text-slate-500">Calculating ATS score and improvement tips…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        {error}
      </div>
    );
  }

  if (!analysis) return null;

  const scoreColor =
    analysis.atsScore >= 75
      ? 'text-green-600'
      : analysis.atsScore >= 50
        ? 'text-amber-600'
        : 'text-red-600';

  return (
    <div className="mt-4 rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50/80 to-white overflow-hidden">
      <div className="px-4 py-3 border-b border-orange-100 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-orange-500" />
        <span className="text-sm font-semibold text-slate-900">Resume AI Preview</span>
      </div>

      <div className="p-4 relative">
        <div className="select-none pointer-events-none blur-[5px] opacity-75 space-y-4">
          <div className="flex items-center gap-5">
            <div className="text-center min-w-[88px] p-3 bg-white rounded-xl border border-slate-200">
              <span className={`text-4xl font-bold ${scoreColor}`}>{analysis.atsScore}</span>
              <p className="text-xs text-slate-500 mt-1">ATS Score</p>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed flex-1">{analysis.atsSummary}</p>
          </div>

          {analysis.improvements.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-900">Improvement feedback</p>
              {analysis.improvements.slice(0, 3).map((item, i) => (
                <div key={`${item.area}-${i}`} className="border-l-2 border-orange-200 pl-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.area}</span>
                    <Badge className={`text-xs ${severityColor[item.severity]}`}>
                      {item.severity}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">{item.suggestion}</p>
                </div>
              ))}
            </div>
          )}

          {analysis.keywordSuggestions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {analysis.keywordSuggestions.slice(0, 5).map((kw) => (
                <Badge key={kw} variant="secondary" className="text-xs">
                  {kw}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/55 backdrop-blur-[1px] rounded-lg mx-2 mb-2">
          <Lock className="w-7 h-7 text-orange-500 mb-2" />
          <p className="text-sm font-semibold text-slate-800 text-center px-4">{overlayMessage}</p>
          <p className="text-xs text-slate-600 text-center px-6 mt-1 max-w-sm">{subtitle}</p>
        </div>
      </div>
    </div>
  );
};

export default ResumeAnalysisBlurredPreview;
